import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { map, switchMap } from 'rxjs/operators';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { deserialize } from 'serialize-ts';
import { queryBuilder } from '@metromarkets/sdk';
import { MetaData, Product, QueryOptions, SeoContent } from '~shared/models';
import { DEFAULT_LIMIT } from '~app/modules/generic/constants';
import { PaginatedProductsResponse, SearchResponse } from '~app/modules/catalog/models';
import { API_PRODUCTS_LIST_ROUTE_V3 } from '~app/modules/catalog/constants';
import { API_CUSTOM_PLP_ROUTE, API_SEO_CONTENT_ROUTE } from '~app/modules/filters/constants';
import { SearchHttpService } from '~app/modules/generic/services/search-http.service';
import { CustomPlpState } from '~app/modules/catalog/store';
import { EXPERIMENT_VARIATIONS, PRODUCT_TYPES_EXPERIMENT_KEY } from '~shared/constants';
import { OptimizelyService } from '~shared/services/optimizely.service';
import { SearchVersionsEnum } from '~app/modules/catalog/services/search-versions.enum';
import { SortOption } from '~shared/modules/application-event/models';
import { RouterStateUrl } from '~core/store';
import { FilterOptionsUtil } from '~app/modules/catalog/utils';
import { Router } from '@angular/router';
import { NavigationHelper } from '~shared/services/navigation-helper.service';
import { ProductsListUrlService } from '~app/modules/generic/services/products-list-url.service';
import { RegionSelectorService } from '~shared/modules/region-selector/services';

@Injectable({
  providedIn: 'root',
})
export class CatalogService {
  constructor(
    private http: HttpClient,
    private searchHttpService: SearchHttpService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private navigationHelper: NavigationHelper,
    private router: Router,
    private optimizelyService: OptimizelyService,
    private productsListUrlService: ProductsListUrlService,
    private readonly regionSelectorService: RegionSelectorService
  ) {}

  getProducts(queryOptions: QueryOptions = {}): Observable<SearchResponse> {
    if (this.productsListUrlService.isSearchPage()) {
      return this.getProductsWithABExperiment(queryOptions);
    }

    const params = this.getProductsAPIHTTPParams({ ...queryOptions });
    return this.requestPaginatedProducts(params);
  }

  getProductsWithABExperiment(queryOptions: QueryOptions = {}): Observable<SearchResponse> {
    return this.optimizelyService.getExperimentVariant(PRODUCT_TYPES_EXPERIMENT_KEY).pipe(
      switchMap((variant: EXPERIMENT_VARIATIONS) => {
        const params = this.getProductsAPIHTTPParams(
          {
            ...queryOptions,
          },
          SearchVersionsEnum[variant]
        );
        return this.requestPaginatedProducts(params);
      })
    );
  }

  getSeoContent(categoryId: string): Observable<SeoContent> {
    return this.http.get<SeoContent>(`${API_SEO_CONTENT_ROUTE()}/${categoryId}`);
  }

  getCustomPlpConfig(slug: string): Observable<CustomPlpState> {
    return this.http.get<CustomPlpState>(`${API_CUSTOM_PLP_ROUTE()}/${slug}`);
  }

  private getProductsAPIHTTPParams(queryOptions: QueryOptions = {}, version: string = null): HttpParams {
    const { offset = 0, limit = DEFAULT_LIMIT, ...restQuery } = queryOptions;

    const params = {
      ...(version && { version }),
      offset,
      limit,
      ...restQuery,
      ...{ useRecommendedProductsParser: '1' },
      ...{ useCharacteristics: '1' },
    };

    return queryBuilder.toParams(params) as HttpParams;
  }

  updateParamsOnSort([sortOption, routerStateUrl]: [SortOption, RouterStateUrl]): void {
    const queryParams = Object.assign({}, routerStateUrl?.queryParams);

    if (queryParams?.filter?.options) {
      queryParams.filter = {
        ...queryParams.filter,
        options: FilterOptionsUtil.formatFilterOptions(queryParams.filter.options),
      };
    }

    if (queryParams?.filter?.categories) {
      queryParams.filter = {
        ...queryParams.filter,
        categories: FilterOptionsUtil.formatFilterOptions({ options: queryParams.filter.categories }).options,
      };
    }

    if (queryParams?.filter?.categoryIds) {
      queryParams.filter = {
        ...queryParams.filter,
        categoryIds: FilterOptionsUtil.formatFilterOptions({ options: queryParams.filter.categoryIds }).options,
      };
    }

    this.navigationHelper.replaceUrl(
      [this.getCurrentPath()],
      { queryParams: { ...queryParams, orderBy: sortOption?.key, order: sortOption?.value } },
      { resetPaging: true }
    );
  }

  private getCurrentPath() {
    return this.router.url.split('?')[0].slice(1);
  }

  private requestPaginatedProducts(params) {
    return this.searchHttpService
      .getPaginatedProducts(API_PRODUCTS_LIST_ROUTE_V3(), params, 'angular_catalog_service')
      .pipe(map(this.mapSearchResponse));
  }

  private mapSearchResponse(paginatedProductsResponse: PaginatedProductsResponse): SearchResponse {
    let recommendedProducts = null;

    if (paginatedProductsResponse.recommendedProducts) {
      recommendedProducts = {
        items: paginatedProductsResponse.recommendedProducts.items.map((product) => Product.map(product)),
        type: paginatedProductsResponse.recommendedProducts.type,
      };
    }

    return {
      correction: paginatedProductsResponse.correction,
      products: {
        totalCount: paginatedProductsResponse.totalCount,
        items: paginatedProductsResponse.items.map((item) => Product.map(item)),
      },
      recommendedProducts,
      metaData: paginatedProductsResponse.meta && deserialize(paginatedProductsResponse.meta, MetaData),
    };
  }
}
